import { useEffect, useState } from 'react';
import { useGraphStore } from '@/store/graphStore';
import { threatColor } from '@/types/dns';

interface SslCert {
  subject_cn: string; issuer_cn: string; days_remaining: number | null;
  is_wildcard: boolean; is_self_signed: boolean; key_algorithm: string; key_bits: number;
}

function ThreatBadge({ score }: { score: number }) {
  const c = threatColor(score);
  const label = score < 20 ? 'CLEAN' : score < 50 ? 'SUSPICIOUS' : score < 80 ? 'HIGH RISK' : 'MALICIOUS';
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold"
      style={{ background: `${c}22`, color: c, border: `1px solid ${c}55` }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c }} />
      {score} — {label}
    </span>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-b pb-2 mb-2" style={{ borderColor: '#1e2830' }}>
      <div className="text-xs uppercase tracking-wider mb-1.5" style={{ color: '#8b949e' }}>{title}</div>
      {children}
    </div>
  );
}

function RecordList({ label, items }: { label: string; items: string[] }) {
  if (!items.length) return null;
  return (
    <div className="mb-1">
      <span className="text-xs font-bold mr-1" style={{ color: '#8b949e' }}>{label}</span>
      <div className="flex flex-wrap gap-1 mt-0.5">
        {items.map(v => (
          <span key={v} className="text-xs font-mono px-1 rounded" style={{ background: '#1c2a1c', color: '#56d364', border: '1px solid #56d36433' }}>{v}</span>
        ))}
      </div>
    </div>
  );
}

export function ScanResultsPanel() {
  const result = useGraphStore(s => s.lastScanResult);
  const target = useGraphStore(s => s.lastScanTarget);
  const [collapsed, setCollapsed] = useState(false);
  const [ssl, setSsl] = useState<SslCert | null | 'loading'>('loading');
  const [historyOpen, setHistoryOpen] = useState(false);
  const scanHistory = useGraphStore(s => s.scanHistory);

  useEffect(() => {
    if (!target || !result) { setSsl(null); return; }
    const r = result as Record<string, unknown>;
    const isIp = !!(r.ip && !r.domain);
    if (isIp) { setSsl(null); return; }
    setSsl('loading');
    let cancelled = false;
    fetch(`/api/v1/intel/ssl-cert/${encodeURIComponent(target)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) setSsl(d?.cert ?? null); })
      .catch(() => { if (!cancelled) setSsl(null); });
    return () => { cancelled = true; };
  }, [target, result]);

  if (!result || !target) return null;

  const r = result as Record<string, unknown>;

  // Unwrap nested structures
  const dns = ((r.dns as Record<string, unknown>)?.dns ?? {}) as Record<string, unknown>;
  const rs = ((r.risk_score as Record<string, unknown>)?.risk_score ?? {}) as Record<string, unknown>;
  const threat = (r.threat ?? {}) as Record<string, unknown>;
  const ipIntel = (r.ip_intel ?? {}) as Record<string, unknown>;
  const geo = (ipIntel.geo ?? {}) as Record<string, unknown>;

  const score = rs.score != null ? Number(rs.score) : null;
  const level = (rs.level as string | undefined) ?? '';
  const flags = (rs.flags as string[] | undefined) ?? [];
  const aRecords = (dns.a_records as string[] | undefined) ?? [];
  const aaaaRecords = (dns.aaaa_records as string[] | undefined) ?? [];
  const nsRecords = (dns.ns_records as string[] | undefined) ?? [];
  const mxRecords = ((dns.mx_records as Array<{ host: string }> | undefined) ?? []).map(m => m.host ?? String(m));
  const cnameRecords = (dns.cname_records as string[] | undefined) ?? [];
  const txtRecords = (dns.txt_records as string[] | undefined) ?? [];
  const threatSources = (threat.sources as string[] | undefined) ?? [];
  const isMalicious = !!(threat.is_malicious);
  const networkType = (ipIntel.network_type as string | undefined) ?? '';
  const repScore = ipIntel.reputation_score != null ? Number(ipIntel.reputation_score) : null;

  const daysLeft = ssl && ssl !== 'loading' ? ssl.days_remaining : null;
  const sslColor = daysLeft == null ? '#8b949e' : daysLeft <= 0 ? '#f85149' : daysLeft <= 30 ? '#f0883e' : '#56d364';

  return (
    <div
      className="absolute left-0 top-0 bottom-0 flex flex-col z-10 overflow-hidden"
      style={{ width: collapsed ? 36 : 280, background: '#161b22', borderRight: '1px solid #30363d', transition: 'width 0.2s' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-2 py-2 border-b flex-shrink-0" style={{ borderColor: '#30363d' }}>
        {!collapsed && (
          <span className="text-xs font-mono font-bold truncate" style={{ color: '#58a6ff', maxWidth: 200 }}>
            {target}
          </span>
        )}
        <button onClick={() => setCollapsed(v => !v)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8b949e', fontSize: 14, padding: '0 2px', marginLeft: collapsed ? 'auto' : 0 }}>
          {collapsed ? '›' : '‹'}
        </button>
      </div>

      {!collapsed && (
        <div className="flex-1 overflow-y-auto px-3 py-2 text-xs font-mono">

          {/* Threat score */}
          {score != null && (
            <Section title="Risk Score">
              <ThreatBadge score={score} />
              {level && <span className="ml-2 text-xs" style={{ color: '#8b949e' }}>{level}</span>}
              {flags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {flags.map(f => (
                    <span key={f} className="text-xs px-1 rounded" style={{ background: '#f0883e18', color: '#f0883e', border: '1px solid #f0883e33' }}>
                      {f.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              )}
            </Section>
          )}

          {/* Threat intel */}
          {(isMalicious || threatSources.length > 0) && (
            <Section title="Threat Intel">
              {isMalicious ? <span style={{ color: '#f85149' }}>⚠ MALICIOUS</span> : null}
              {threatSources.map(s => (
                <span key={s} className="block text-xs" style={{ color: '#f0883e' }}>• {s}</span>
              ))}
            </Section>
          )}

          {/* DNS Records */}
          {(aRecords.length > 0 || aaaaRecords.length > 0 || nsRecords.length > 0 || mxRecords.length > 0 || cnameRecords.length > 0) && (
            <Section title="DNS Records">
              <RecordList label="A" items={aRecords} />
              <RecordList label="AAAA" items={aaaaRecords} />
              <RecordList label="NS" items={nsRecords} />
              <RecordList label="MX" items={mxRecords} />
              <RecordList label="CNAME" items={cnameRecords} />
              {txtRecords.length > 0 && (
                <div className="mb-1">
                  <span className="text-xs font-bold mr-1" style={{ color: '#8b949e' }}>TXT</span>
                  <span className="text-xs" style={{ color: '#8b949e' }}>{txtRecords.length} record{txtRecords.length !== 1 ? 's' : ''}</span>
                </div>
              )}
            </Section>
          )}

          {/* IP / Geo */}
          {geo.country_code ? (
            <Section title="IP Intel">
              {ipIntel.ip_address ? <div style={{ color: '#c9d1d9' }}>{String(ipIntel.ip_address)}</div> : null}
              <div style={{ color: '#8b949e' }}>{geo.city ? `${String(geo.city)}, ` : ''}{String(geo.country ?? '')}</div>
              {networkType ? <div style={{ color: '#58a6ff' }}>{networkType}</div> : null}
              {repScore != null ? <div style={{ color: repScore >= 80 ? '#56d364' : '#f0883e' }}>rep: {repScore}</div> : null}
            </Section>
          ) : null}

          {/* SSL */}
          <Section title="SSL Certificate">
            {ssl === 'loading' && <span style={{ color: '#8b949e' }}>checking…</span>}
            {ssl === null && <span style={{ color: '#8b949e' }}>none on file</span>}
            {ssl && ssl !== 'loading' && (
              <div className="flex flex-col gap-0.5">
                <span style={{ color: '#c9d1d9' }}>{ssl.subject_cn}</span>
                <span style={{ color: '#8b949e' }}>issuer: {ssl.issuer_cn}</span>
                <span style={{ color: sslColor }}>
                  {daysLeft == null ? '—' : daysLeft <= 0 ? `expired ${Math.abs(daysLeft)}d ago` : `${daysLeft}d left`}
                </span>
                <span style={{ color: '#8b949e' }}>{ssl.key_algorithm} {ssl.key_bits}-bit{ssl.is_wildcard ? ' · wildcard' : ''}{ssl.is_self_signed ? ' · self-signed' : ''}</span>
              </div>
            )}
          </Section>

          {/* Scan history */}
          {scanHistory.length > 0 && (
            <Section title="Recent Scans">
              <button onClick={() => setHistoryOpen(v => !v)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8b949e', fontSize: 11, padding: 0 }}>
                {historyOpen ? '▲' : '▼'} {scanHistory.length} scan{scanHistory.length !== 1 ? 's' : ''}
              </button>
              {historyOpen && (
                <div className="mt-1 flex flex-col gap-0.5" style={{ maxHeight: 160, overflowY: 'auto' }}>
                  {scanHistory.map(h => (
                    <div key={h.jobId} className="flex items-center justify-between gap-1 px-1 py-0.5 rounded"
                      style={{ background: '#0d1117' }}>
                      <span className="truncate text-xs" style={{ color: '#c9d1d9', maxWidth: 160 }}>{h.target}</span>
                      {h.threatScore != null && (
                        <span className="text-xs flex-shrink-0" style={{ color: threatColor(h.threatScore) }}>{h.threatScore}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Section>
          )}
        </div>
      )}
    </div>
  );
}
